import { contentFormats } from "@ondit/publishing";
import { Button, ChipInput, ChipModal, ChipModalBody, ChipModalHeader, ChipSelect, NativeSelect } from "@ondit/ui";
import { Search } from "@ondit/ui/icons";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api } from "~/core/api/index";
import { language as languageName } from "~/core/format";
import { pick, useI18n, useT } from "~/core/i18n";
import { brandContracts } from "~/core/publishing";
import { keys } from "~/core/query";
import { Async } from "~/core/ui/states";
import { byValue, matches, PlatformRow } from "~/modules/analytics/features/platform-publishing/platforms";
export type ChannelSelection = { platform_id: string; format: string };
export function ChannelPicker({
	entityId,
	open,
	onClose,
	onConfirm,
	initialSelection,
	confirmLabel,
	allowEmpty = false,
	filterOnly = false,
}: {
	entityId: string;
	open: boolean;
	onClose: () => void;
	onConfirm: (channels: ChannelSelection[]) => void;
	initialSelection?: ChannelSelection[];
	confirmLabel?: string;
	allowEmpty?: boolean;
	filterOnly?: boolean;
}) {
	const { locale } = useI18n();
	const t = useT();
	const tr = (en: string, zh: string) => pick(locale, [en, zh]);
	const [query, setQuery] = useState("");
	const [languagesSelected, setLanguages] = useState<string[]>([]);
	const [categories, setCategories] = useState<string[]>([]);
	const [industries, setIndustries] = useState<string[]>([]);
	const [tiers, setTiers] = useState<string[]>([]);
	const [regionsSelected, setRegions] = useState<string[]>([]);
	const [selected, setSelected] = useState<Record<string, string>>({});
	useEffect(() => {
		if (open && initialSelection)
			setSelected(Object.fromEntries(initialSelection.map((c) => [c.platform_id, c.format])));
	}, [open]);
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "distribution"],
		queryFn: () => api.distribution(entityId),
		enabled: open,
	});
	const contracts = brandContracts(state.data?.platforms);
	const channels = state.data?.platforms ?? [];
	const languages = [...new Set(channels.flatMap((c) => c.languages ?? []))].sort();
	const regions = [...new Set(channels.map((c) => c.region).filter(Boolean))].sort();
	const name = (id: string) => {
		const term = state.data?.taxonomy.categories.find((c) => c.id === id);
		return term ? pick(locale, term.label) : id;
	};
	const shown = channels
		.filter(
			(c) =>
				matches(c, { languages: languagesSelected, categories, industries, tiers }) &&
				(!regionsSelected.length || regionsSelected.includes(c.region)) &&
				(!query.trim() ||
					[c.name, c.domain, c.summary ? pick(locale, c.summary) : "", ...(c.categories ?? []).map(name)]
						.join(" ")
						.toLocaleLowerCase()
						.includes(query.trim().toLocaleLowerCase())),
		)
		.sort(byValue);
	return (
		<ChipModal
			open={open}
			onOpenChange={(value) => {
				if (!value) onClose();
			}}
			srTitle={tr("Add channels", "添加渠道")}
			size="xl"
		>
			<ChipModalHeader onClose={onClose} closeAriaLabel={t("common.close")}>
				{tr("Add channels", "添加渠道")}
			</ChipModalHeader>
			<ChipModalBody>
				<div className="channel-picker">
					<div className="channel-picker-filters">
						{(
							[
								["categories", t("publishing.category"), categories, setCategories],
								["industries", t("publishing.industry"), industries, setIndustries],
								["tiers", t("publishing.value"), tiers, setTiers],
							] as const
						).map(([group, label, values, onChange]) => (
							<ChipSelect
								key={group}
								multiSelect
								showAllOption
								allOptionLabel={label}
								aria-label={label}
								multiSelectValues={values}
								onMultiSelectChange={onChange}
								options={(state.data?.taxonomy[group] ?? []).map((term) => ({
									value: term.id,
									label: pick(locale, term.label),
								}))}
							/>
						))}
						<ChipSelect
							multiSelect
							showAllOption
							allOptionLabel={t("publishing.language")}
							aria-label={t("publishing.language")}
							multiSelectValues={languagesSelected}
							onMultiSelectChange={setLanguages}
							options={languages.map((code) => ({ value: code, label: languageName(locale, code) }))}
						/>
						<ChipSelect
							multiSelect
							showAllOption
							allOptionLabel={tr("Region", "地区")}
							aria-label={tr("Region", "地区")}
							multiSelectValues={regionsSelected}
							onMultiSelectChange={setRegions}
							options={regions.map((code) => ({
								value: code,
								label: state.data?.taxonomy.regions.find((term) => term.id === code)?.label
									? pick(locale, state.data!.taxonomy.regions.find((term) => term.id === code)!.label)
									: code,
							}))}
						/>
						<ChipInput
							icon={Search}
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							placeholder={t("distribution.search")}
							aria-label={t("distribution.search")}
						/>
					</div>
					<Async state={state}>
						{() => (
							<ul className="publishing-platforms channel-picker-list">
								{shown.map((c) => {
									const contract = contracts.byId(c.id);
									const formats = contract ? contentFormats(contract) : [];
									return (
										<PlatformRow key={c.id} platform={c}>
											<div className="channel-picker-selection">
												<label className="row-actions">
													<input
														type="checkbox"
														checked={c.id in selected}
														aria-label={tr(`Select ${c.name}`, `选择 ${c.name}`)}
														disabled={
															(!filterOnly && !formats.length) ||
															(!filterOnly && !(c.id in selected) && Object.keys(selected).length >= 30)
														}
														onChange={(e) =>
															setSelected((old) => {
																const next = { ...old };
																if (e.target.checked) next[c.id] = formats[0]?.id ?? "";
																else delete next[c.id];
																return next;
															})
														}
													/>
													{tr("Select", "选择")}
												</label>
												{!formats.length && (
													<small className="hint">{tr("Content format not configured", "尚未配置内容形式")}</small>
												)}

												{c.id in selected && formats.length > 1 && (
													<NativeSelect
														aria-label={tr(`${c.name} content format`, `${c.name} 内容形式`)}
														value={selected[c.id]}
														onChange={(e) => setSelected({ ...selected, [c.id]: e.target.value })}
													>
														{formats.map((f) => (
															<option key={f.id} value={f.id}>
																{pick(locale, f.label)}
															</option>
														))}
													</NativeSelect>
												)}
											</div>
										</PlatformRow>
									);
								})}
								{!shown.length && <li className="publishing-platform hint">{t("table.noMatch")}</li>}
							</ul>
						)}
					</Async>
					<div className="smart-row">
						<span>{tr(`${Object.keys(selected).length} selected`, `已选 ${Object.keys(selected).length} 个渠道`)}</span>
						<Button
							variant="primary"
							disabled={(!allowEmpty && !Object.keys(selected).length) || state.isPending || !!state.error}
							onClick={() =>
								onConfirm(Object.entries(selected).map(([platform_id, format]) => ({ platform_id, format })))
							}
						>
							{confirmLabel ?? tr("Add selected channels", "添加所选渠道")}
						</Button>
					</div>
				</div>
			</ChipModalBody>
		</ChipModal>
	);
}
